import type {
  BloomMenuTheme,
  CustomSEO,
  PartnerScript,
} from '@iheartjane/data-access';
import type { CategoryImageKey } from '@iheartjane/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';

import type {
  BloomFooterTemplateFormState,
  BloomOperatorConfig,
} from '@jane/business-admin/types';
import { janeApiV2 } from '@jane/shared/data-access';

type BloomUpdateParams = {
  category_images?: {
    images: {
      [key in CategoryImageKey]?: string | null;
    };
    status: 'draft' | 'published';
  };
  custom_seo?: CustomSEO;
  footer?: BloomFooterTemplateFormState;
  google_sso_client_id?: string;
  google_sso_client_secret?: string;
  google_tag_manager_container_id?: string;
  logo?: string;
  partner_scripts?: PartnerScript[];
  theme?: BloomMenuTheme['bloom'];
  theme_draft?: BloomMenuTheme['bloom'];
};

const bloomMenusUrl = () => `/managers/bloom_menus`;
const bloomMenuByStoreIdUrl = (storeId: string) =>
  `/managers/bloom_menus?store_id=${storeId}&sort=updated_at&order=desc`;

const bloomMenuUrl = (menuId: number) => `/managers/bloom_menus/${menuId}`;

export const saveCurrentBloomMenuId = (menu_id: number | string) => {
  return window.sessionStorage.setItem('current_bloom_menu', `${menu_id}`);
};

const getCurrentBloomMenuId = () => {
  return window.sessionStorage.getItem('current_bloom_menu');
};

export const useCurrentBloomMenuId = () => {
  const { menu_id } = useParams();
  const sessionMenuId = getCurrentBloomMenuId();
  return Number(menu_id ?? sessionMenuId);
};

const fetchBloomMenus = async (): Promise<BloomOperatorConfig[]> => {
  const data = await janeApiV2.get<{ bloom_menus: BloomOperatorConfig[] }>(
    bloomMenusUrl()
  );

  return data['bloom_menus'];
};

export const useFetchBloomMenus = <TResult = BloomOperatorConfig[]>({
  select,
}: {
  select?: (configs: BloomOperatorConfig[]) => TResult;
} = {}) => {
  return useQuery({
    queryFn: () => fetchBloomMenus(),
    queryKey: ['bloom_menus'],
    select,
  });
};

export const useFetchCurrentBloomMenu = () => {
  const menuId = useCurrentBloomMenuId();

  return useFetchBloomMenus({
    select: (configs): BloomOperatorConfig | undefined =>
      menuId
        ? (configs.find((menu) => menu.id === menuId) ?? configs[0])
        : configs[0],
  });
};

const fetchBloomMenusByStoreId = async (storeId: string) => {
  const data = await janeApiV2.get<{ bloom_menus: BloomOperatorConfig[] }>(
    bloomMenuByStoreIdUrl(storeId)
  );

  return data['bloom_menus'];
};

export const useFetchBloomMenusByStoreId = (storeId: string) => {
  return useQuery({
    queryFn: () => fetchBloomMenusByStoreId(storeId),
    queryKey: ['bloom_menu_by_store_id', storeId],
    enabled: !!storeId,
  });
};

const updateBloomMenu = async (
  menuId?: number,
  updates: BloomUpdateParams = {}
) => {
  if (!menuId) {
    return null;
  }

  return await janeApiV2.patch(bloomMenuUrl(menuId), updates);
};

export const useUpdateBloomMenu = (menuId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: BloomUpdateParams = {}) =>
      await updateBloomMenu(menuId, args),
    mutationKey: ['update-bloom-menu', menuId],
    onError: console.error,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['bloom_menus'],
        exact: true,
      });
    },
  });
};
